import { useEffect, useRef, useState, type ClipboardEvent, type FormEvent, type KeyboardEvent } from 'react';
import { TriangleAlert } from 'lucide-react';
import { ErrorCode, portalCodeLength } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { ApiError, errorMessage } from '../../../lib/api';
import { portalLoginCode, portalResendCode } from '../api';
import styles from '../frame/Phone.module.css';
import type { PortalCodeChallenge, PortalLoginSession } from '../types';
import local from './Login.module.css';

function countdownLabel(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

/** Six 54 px boxes (Portal 12): auto-advance, backspace goes back, a pasted code fills every box. */
function CodeBoxes({
  value,
  onChange,
  invalid,
  describedBy,
}: {
  value: string;
  onChange: (value: string) => void;
  invalid: boolean;
  describedBy?: string | undefined;
}) {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length: portalCodeLength }, (_, index) => value.charAt(index));

  function focusBox(index: number) {
    const target = inputs.current[Math.max(0, Math.min(portalCodeLength - 1, index))];
    target?.focus();
    target?.select();
  }

  function setDigit(index: number, typed: string) {
    const clean = typed.replace(/\D/g, '');
    if (clean.length > 1) {
      const next = (value.slice(0, index) + clean).slice(0, portalCodeLength);
      onChange(next);
      focusBox(next.length);
      return;
    }
    if (!clean) return;
    const at = Math.min(index, value.length);
    const next = (value.slice(0, at) + clean + value.slice(at + 1)).slice(0, portalCodeLength);
    onChange(next);
    focusBox(at + 1);
  }

  function onKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (digits[index]) onChange(value.slice(0, index) + value.slice(index + 1));
      else if (index > 0) {
        onChange(value.slice(0, index - 1) + value.slice(index));
        focusBox(index - 1);
      }
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusBox(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusBox(index + 1);
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, portalCodeLength);
    if (!pasted) return;
    event.preventDefault();
    onChange(pasted);
    focusBox(pasted.length);
  }

  return (
    <div className={local.codeGrid} role="group" aria-label="Bestätigungscode" aria-describedby={describedBy}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            inputs.current[index] = element;
          }}
          className={clsx(local.codeBox, invalid && local.codeBoxInvalid)}
          value={digit}
          onChange={(event) => setDigit(index, event.target.value)}
          onKeyDown={(event) => onKeyDown(index, event)}
          onPaste={onPaste}
          onFocus={(event) => event.target.select()}
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          pattern="[0-9]*"
          maxLength={portalCodeLength}
          aria-label={`Ziffer ${index + 1} von ${portalCodeLength}`}
          aria-invalid={invalid ? true : undefined}
          autoFocus={index === 0}
        />
      ))}
    </div>
  );
}

/** Mandantenportal 12 „Bestätigungscode" (the 202 of the login): countdown, resend, „Bestätigen" at six digits. */
export function CodeStep({
  slug,
  challenge,
  onChallenge,
  onSession,
  onRestart,
}: {
  slug: string;
  challenge: PortalCodeChallenge;
  onChallenge: (challenge: PortalCodeChallenge) => void;
  onSession: (session: PortalLoginSession) => void;
  onRestart: () => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [pending, setPending] = useState(false);
  const [resending, setResending] = useState(false);
  const [seconds, setSeconds] = useState(challenge.resendInSeconds);

  useEffect(() => {
    setSeconds(challenge.resendInSeconds);
  }, [challenge]);

  useEffect(() => {
    if (seconds <= 0) return undefined;
    const timer = window.setTimeout(() => setSeconds((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [seconds]);

  async function confirm(event: FormEvent) {
    event.preventDefault();
    if (code.length !== portalCodeLength) return;
    setPending(true);
    setError(null);
    try {
      onSession(await portalLoginCode(slug, challenge.challengeId, code));
    } catch (cause) {
      setError(errorMessage(cause));
      const wrongCode = cause instanceof ApiError && cause.detailNumber('remainingAttempts') !== null;
      setExpired(!wrongCode && cause instanceof ApiError && cause.code === ErrorCode.PORTAL_CODE_REQUIRED);
      setCode('');
    } finally {
      setPending(false);
    }
  }

  async function resend() {
    setResending(true);
    setError(null);
    try {
      const next = await portalResendCode(slug, challenge.challengeId);
      setExpired(false);
      setCode('');
      onChallenge(next);
    } catch (cause) {
      setError(errorMessage(cause));
      const wait = cause instanceof ApiError ? cause.detailNumber('resendInSeconds') : null;
      if (wait !== null) setSeconds(wait);
    } finally {
      setResending(false);
    }
  }

  const target =
    challenge.channel === 'sms'
      ? `Wir haben Ihnen eine sechsstellige Zahl an ${challenge.maskedTarget} geschickt.`
      : `Wir haben Ihnen eine sechsstellige Zahl per E-Mail an ${challenge.maskedTarget} geschickt.`;

  return (
    <form className={local.form} onSubmit={(event) => void confirm(event)} noValidate>
      <div className={local.codeHead}>
        <h1 className={styles.title}>Bestätigungscode</h1>
        <p className={styles.text}>{target}</p>
      </div>
      <div className={local.codeBody}>
        <CodeBoxes
          value={code}
          onChange={setCode}
          invalid={error !== null}
          describedBy={error ? 'portal-code-error' : undefined}
        />
      </div>
      {error ? (
        <div className={local.codeError} id="portal-code-error" role="alert">
          <TriangleAlert size={14} strokeWidth={1.75} aria-hidden="true" />
          <span>{error}</span>
        </div>
      ) : null}
      <div className={local.resend}>
        {expired ? (
          <button type="button" className={styles.link} onClick={onRestart}>
            Zurück zur Anmeldung
          </button>
        ) : seconds > 0 ? (
          <span aria-live="off">Code erneut senden in {countdownLabel(seconds)}</span>
        ) : (
          <button type="button" className={styles.link} onClick={() => void resend()} disabled={resending}>
            Code erneut senden
          </button>
        )}
      </div>
      <div className={`${styles.footer} ${local.footerCta}`}>
        <button
          type="submit"
          className={`pressable ${styles.primary}`}
          disabled={code.length !== portalCodeLength || pending}
        >
          Bestätigen
        </button>
      </div>
    </form>
  );
}
